import { invalidate } from '$app/navigation';
import { page } from '$app/state';
import type { CitationAttrs } from '$lib/markdown/types';
import { processStreamedResponse } from '$lib/process-streamed-response';
import { handleApiError } from '@astlai/common/util/error-handling';
import { Fetchable } from '@astlai/common/util/fetchable';
import { nid } from '@astlai/common/util/nanoid';
import { citationHash } from 'src/contexts/citation-registry.context.svelte';
import { PbService } from 'src/services/pb-service.svelte';
import type { ConversationRecord } from 'src/types/conversation';
import {
	ChatMessage,
	type ChunkDTO,
	type CitationOccurrence,
	type CitedSource,
	type DocumentOverview,
	type RetrievalContext,
	type SourceDocument,
	type WebSourceDocument
} from 'src/types/llm';
import { type SourceBlock } from 'src/util/citation-transformer';
import { getHighlightColor, getWebHighlightColor } from 'src/util/colors';
import { mergeRetrievalContext, mergeSources, mergeWebSources } from 'src/util/merge-sources';
import { injectHashesIntoText, parseCitationsFromText } from 'src/util/parse-citations';
import { getContext, setContext, untrack } from 'svelte';

export enum ChatStep {
	INTERVIEW,
	IMAGE_GENERATION,
	UPLOAD_INTENTION,
	UPLOAD_CONFIRMATION,
	UPLOAD_FINISHED
}

export class ChatContext {
	private initialMessage = `Hallo, wie kann ich dir helfen?`;
	readonly fetchable = new Fetchable();

	conversation: ConversationRecord = $state();
	private readonly webSearchEnabled: boolean = $derived(
		page.data.loggedInUser?.settings?.webSearchEnabled ?? false
	);
	standalone: boolean = $state(false);
	chatPlaceholder: string | undefined = $state(undefined);
	private currentAbortController: AbortController | null = $state(null);
	readonly isStreaming = $derived(this.currentAbortController !== null);

	readonly presentableMessages = $derived(
		this.conversation?.messages.filter(
			(m) => (m.role !== 'system' && !!m.content) || !!m.reasoning_content
		)
	);

	readonly latestAssistantMessage = $derived(
		this.conversation?.messages.toReversed().find((m) => m.role === 'assistant')
	);

	readonly isReasoning = $derived(
		!!this.latestAssistantMessage &&
			this.latestAssistantMessage.status !== 'completed' &&
			!!this.latestAssistantMessage.reasoning_content &&
			!this.latestAssistantMessage.content
	);

	private citations: CitationAttrs[] = $state([]);

	private rebuildCitations() {
		const seen = new Set<string>();
		// Pure write – no read of this.citations, so the $effect that calls
		// rebuildCitations() doesn't track citations as a dependency.
		this.citations = (this.conversation?.messages ?? [])
			.filter((msg) => msg.role === 'assistant' && !!msg.content)
			.flatMap((msg) => parseCitationsFromText(msg.content!))
			.filter((attrs) => {
				const h = citationHash(attrs);
				if (seen.has(h)) {
					return false;
				}
				seen.add(h);
				return true;
			});
	}

	private registerCitations(text: string) {
		const existing = new Set(this.citations.map((a) => citationHash(a)));
		const newCitations = parseCitationsFromText(text).filter((a) => !existing.has(citationHash(a)));
		if (newCitations.length > 0) {
			this.citations = [...this.citations, ...newCitations];
		}
	}

	private fullHashInjection() {
		this.conversation.messages = this.conversation.messages.map((msg) => {
			if (msg.role !== 'assistant' || !msg.content) {
				return msg;
			}
			return { ...msg, content: injectHashesIntoText(msg.content) };
		});
	}

	// All cited sources derived from parsed citations + conversation metadata.
	readonly citedSources: CitedSource[] = $derived.by(() => {
		// Group citations by resolved source identity.
		// KB citations are grouped by doc_id (multiple chunks → one source entry).
		// Web citations are grouped by web_id.
		const groups = new Map<string, CitationAttrs[]>();

		for (const reg of this.citations) {
			let groupKey: string;
			if (reg.webId != null) {
				groupKey = `web:${reg.webId}`;
			} else {
				const docId =
					reg.docId ??
					this.conversation?.sources?.find(
						(s) => reg.chunkId != null && s.chunk_ids.includes(reg.chunkId)
					)?.doc_id;
				groupKey = `kb:${docId ?? reg.chunkId ?? 'unknown'}`;
			}
			if (!groups.has(groupKey)) {
				groups.set(groupKey, []);
			}
			groups.get(groupKey)!.push(reg);
		}

		const result: CitedSource[] = [];
		let kbIdx = 0;
		let webIdx = 0;

		for (const [groupKey, regs] of groups) {
			const isWeb = groupKey.startsWith('web:');
			const citation_index = isWeb ? ++webIdx : ++kbIdx;
			const citation_color = isWeb
				? getWebHighlightColor(webIdx - 1)
				: getHighlightColor(kbIdx - 1);

			const quotedCount = regs.filter((r) => r.quote != null).length;
			const shouldAddSubIndices = quotedCount >= 1 && regs.length >= 2;
			let quotedIdx = 0;
			const occurrences: CitationOccurrence[] = regs.map((reg) => {
				const type = reg.webId != null ? 'web' : reg.chunkId != null ? 'chunk' : 'document';
				const id = reg.webId ?? reg.chunkId ?? reg.docId ?? 0;
				const sub_index =
					shouldAddSubIndices && reg.quote != null ? String.fromCharCode(65 + quotedIdx++) : '';
				return {
					hash: citationHash(reg),
					sub_index,
					quotedText: reg.quote,
					chunkText: reg.chunkId != null ? this.chunkTexts.get(reg.chunkId) : undefined,
					sourceBlocks:
						reg.chunkId != null
							? (this.chunkMap.get(reg.chunkId)?.chunk_meta?.source_blocks as
									| SourceBlock[]
									| undefined)
							: undefined,
					type,
					id
				};
			});

			if (isWeb) {
				const webSrc = this.conversation?.web_sources?.find((s) => s.id === regs[0].webId);
				result.push({
					citation_index,
					citation_color,
					occurrences,
					title: webSrc?.title ?? '',
					web_id: regs[0].webId,
					url: webSrc?.url,
					description: webSrc?.description,
					fetched_at: webSrc?.fetched_at,
					web_source_type: webSrc?.type
				});
			} else {
				const docId =
					regs[0].docId ??
					this.conversation?.sources?.find(
						(s) => regs[0].chunkId != null && s.chunk_ids.includes(regs[0].chunkId)
					)?.doc_id;
				const doc = docId != null ? this.documentMap.get(docId) : undefined;
				const convSource = this.conversation?.sources?.find((s) => s.doc_id === docId);
				result.push({
					citation_index,
					citation_color,
					occurrences,
					title: doc?.res_name ?? '',
					doc_id: docId,
					chunk_ids: convSource?.chunk_ids ?? []
				});
			}
		}

		return result;
	});

	readonly citationInfoMap = $derived(
		new Map(
			this.citedSources.flatMap((source) =>
				source.occurrences.map((occurrence) => [occurrence.hash, { source, occurrence }] as const)
			)
		)
	);

	getCitationInfo(hash: string): { source: CitedSource; occurrence: CitationOccurrence } | null {
		return this.citationInfoMap.get(hash) ?? null;
	}

	// Lookup maps for documents and chunks
	readonly documentMap = $derived(
		new Map<number, DocumentOverview>(
			(this.conversation?.retrievalContext?.documents ?? []).map((d) => [d.id, d])
		)
	);

	readonly chunkMap = $derived(
		new Map<number, ChunkDTO>(
			(this.conversation?.retrievalContext?.chunks ?? []).map((c) => [c.id, c])
		)
	);

	readonly chunkTexts = $derived(
		new Map<number, string>(
			(this.conversation?.retrievalContext?.chunks ?? []).map((c) => [c.id, c.text])
		)
	);

	private pbClient: PbService;

	constructor(baseUrl?: string, apiKey?: string, initialConvo?: ConversationRecord) {
		// we need a specific instance of pbservice here, because it might be instantiated by the standalone component
		this.pbClient = new PbService(baseUrl, apiKey);
		this.conversation = initialConvo;
		$effect(() => {
			// Rebuild citation index when the active conversation changes (covers initial load
			// and navigation between conversations). The conversation id is the tracked dependency.
			void this.conversation?.id;
			if (!this.conversation) {
				this.citations = [];
				return;
			}

			untrack(() => {
				this.fullHashInjection();
				this.rebuildCitations();
			});
		});
	}

	/**
	 * Get a completion from the llm, an additional message can be appended. this message will not automatically appear in the message history
	 * @param additionalMessage
	 * @returns
	 */
	private async getCompletion(additionalMessage?: ChatMessage, appendResponse = false) {
		const tempId = nid();
		let accMessage: ChatMessage = {
			created: new Date(),
			id: tempId,
			status: 'pending',
			content: '',
			reasoning_content: '',
			role: 'assistant',
			type: 'message'
		};
		if (appendResponse) {
			this.appendMessage(accMessage);
		}

		// Create abort controller for this request
		const abortController = new AbortController();
		this.currentAbortController = abortController;

		try {
			const response = await this.pbClient.chatRequest(
				this.conversation.id,
				additionalMessage
					? [...this.conversation.messages, additionalMessage]
					: this.conversation.messages,
				this.conversation.model,
				this.conversation.tenant,
				abortController.signal,
				this.webSearchEnabled
			);

			await handleApiError(response);

			const stream = processStreamedResponse(response, accMessage, abortController);

			for await (const {
				eventData,
				eventType,
				message,
				sources,
				webSources,
				retrievalContext
			} of stream) {
				if (eventType === 'conversation.created') {
					this.conversation.id = eventData.id as string;
				}

				if (retrievalContext) {
					this.setRetrievalContext(retrievalContext);
				}

				if (sources) {
					this.appendSources(sources);
				}

				if (webSources) {
					this.appendWebSources(webSources);
				}

				if (eventType === 'rag.topic') {
					const { topic_description } = eventData as { topic_description: string };
					if (!this.conversation.title) {
						this.conversation.title = topic_description;
					}
				}
				if (eventType === 'rag.usage') {
					const { input_tokens, output_tokens } = eventData as {
						input_tokens: number;
						output_tokens: number;
					};
					this.conversation.total_input_tokens =
						(this.conversation.total_input_tokens ?? 0) + input_tokens;
					this.conversation.total_output_tokens =
						(this.conversation.total_output_tokens ?? 0) + output_tokens;
				}
				accMessage = { ...message, content: injectHashesIntoText(message.content) };
				if (appendResponse) {
					this.upsertMessage(accMessage, tempId);
					if (accMessage.content) {
						this.registerCitations(accMessage.content);
					}
				}
			}

			this.rebuildCitations();
			await invalidate('update:conversations');

			return accMessage;
		} catch (e) {
			// Only remove message and re-throw if it wasn't aborted by user
			const isAbortError = e instanceof Error && e.name === 'AbortError';
			if (!isAbortError) {
				this.removeMessage(tempId);
				throw e;
			}
		} finally {
			// Clear abort controller when stream completes
			this.currentAbortController = null;
		}
	}

	upsertMessage(message: ChatMessage, tempId?: string) {
		if (message.id) {
			if (tempId && tempId !== message.id) {
				// Real ID differs from temp - check if temp message still exists
				const tempExists = this.conversation.messages.some((m) => m.id === tempId);
				if (tempExists) {
					return this.updateMessage(tempId, message);
				}

				// Temp message was already replaced, fall through to update by real ID
			}
			return this.updateMessage(message.id, message);
		}

		return this.appendMessage(message);
	}

	appendMessage(message: ChatMessage) {
		const id = message.id || nid();
		this.conversation.messages = [...this.conversation.messages, { ...message, id }];
		return id;
	}

	updateMessage(id: string, messageUpdate: Partial<ChatMessage>) {
		const i = this.conversation.messages.findIndex((m) => m.id === id);
		if (i > -1) {
			this.conversation.messages[i] = { ...this.conversation.messages[i], ...messageUpdate };
			return messageUpdate.id;
		}
		this.appendMessage({ id, ...messageUpdate } as ChatMessage);
	}

	removeMessage(id: string) {
		this.conversation.messages = this.conversation.messages.filter((m) => m.id !== id);
		this.rebuildCitations();
	}

	appendSources(sources: SourceDocument[]) {
		this.conversation.sources = mergeSources([...this.conversation.sources, ...sources]);
	}

	appendWebSources(sources: WebSourceDocument[]) {
		this.conversation.web_sources = mergeWebSources([
			...(this.conversation.web_sources ?? []),
			...sources
		]);
	}

	setRetrievalContext(context: RetrievalContext) {
		this.conversation.retrievalContext = mergeRetrievalContext(
			this.conversation.retrievalContext,
			context
		);
	}

	reset() {
		this.conversation.messages = [];
		this.upsertMessage(new ChatMessage('assistant', this.initialMessage));
	}

	async chat(userMessage?: string) {
		if (userMessage) {
			this.upsertMessage(new ChatMessage('user', userMessage));
		}

		return this.getCompletion(null, true);
	}

	async abort() {
		if (this.currentAbortController) {
			this.currentAbortController.abort();

			// Mark the last assistant message as incomplete
			const lastMessage = this.conversation.messages.at(-1);
			if (lastMessage?.role === 'assistant') {
				this.updateMessage(lastMessage.id, {
					status: 'incomplete'
				});

				// Persist to database
				await this.updateConversation({
					messages: this.conversation.messages
				});
			}

			this.currentAbortController = null;
		}
	}

	async retry() {
		this.fetchable.fetch(async () => {
			if (this.conversation.messages.at(-1)?.role === 'assistant' && !!this.conversation.id) {
				this.conversation.messages.pop();
				await this.updateConversation({
					messages: this.conversation.messages
				});
			}
			this.fetchable.error = null;
			await this.chat();
		});
	}

	async updateConversation(params: Partial<ConversationRecord>) {
		this.conversation.title = params.title || this.conversation.title;
		this.conversation.model = params.model || this.conversation.model;
		this.pbClient.updateConversation(this.conversation.id, params);
		await invalidate(`update:conversation:${this.conversation.id}`);
		await invalidate('update:conversations');
	}
}

export const CHAT_CONTEXT_KEY = Symbol('chat');

export function getChatContext() {
	return getContext<ChatContext>(CHAT_CONTEXT_KEY);
}

export function setChatContext(ctx: ChatContext) {
	setContext(CHAT_CONTEXT_KEY, ctx);
	return ctx;
}
